import { label } from "./format";
import type { MonitorRun } from "./types";

// Pipeline groups in execution order, furthest along first. A run that has
// live stages in several groups is shown at the furthest one.
export const PHASES: { key: string; label: string }[] = [
  { key: "delivery", label: "Delivering" },
  { key: "render", label: "Rendering the video" },
  { key: "assets", label: "Generating clips and voice" },
  { key: "directing", label: "Directing (interview, script, narration)" },
  { key: "intake", label: "Intake" },
];

const WAIT_LABELS: Record<string, string> = {
  delivery_receipt: "the WhatsApp delivery receipt",
  user_input: "the customer's answer",
  provider: "the video provider",
  capacity: "capacity",
  backoff: "a retry backoff",
  dependency: "an earlier stage",
  unknown: "an unknown condition",
};

const LIFECYCLE_LABELS: Record<string, string> = {
  succeeded: "Delivered",
  failed_final: "Failed",
  cancelled: "Cancelled",
  expired: "Expired",
};

export type RunPhase = {
  key: string;
  label: string;
  detail: string;
  order: number;
};

function waitsOf(run: MonitorRun): string[] {
  return Object.entries(run.wait_counts ?? {})
    .filter(([, count]) => count > 0)
    .map(([kind]) => WAIT_LABELS[kind] ?? label(kind));
}

export function runPhase(run: MonitorRun): RunPhase {
  if (run.lifecycle !== "active") {
    const evidence = run.terminal_evidence;
    return {
      key: run.lifecycle,
      label: LIFECYCLE_LABELS[run.lifecycle] ?? label(run.lifecycle),
      detail: evidence
        ? `${label(evidence.authority)} · ${label(evidence.status)}`
        : "",
      order: 9,
    };
  }
  const groups = Object.keys(run.active_groups ?? {});
  const waits = waitsOf(run);
  const phaseIndex = PHASES.findIndex((phase) => groups.includes(phase.key));
  if (phaseIndex >= 0 || groups.length) {
    const phase =
      phaseIndex >= 0
        ? PHASES[phaseIndex]
        : { key: groups[0], label: label(groups[0]) };
    const counts = run.stage_counts ?? {};
    const parts: string[] = [];
    if (counts.running) parts.push(`${counts.running} running`);
    if (counts.waiting)
      parts.push(
        waits.length
          ? `waiting for ${waits.join(", ")}`
          : `${counts.waiting} waiting`,
      );
    if (counts.retrying) parts.push(`${counts.retrying} retrying`);
    if (counts.failed) parts.push(`${counts.failed} failed`);
    const others = groups
      .filter((group) => group !== phase.key)
      .map(
        (group) => PHASES.find((p) => p.key === group)?.label ?? label(group),
      );
    if (others.length) parts.push(`also active: ${others.join(", ")}`);
    return {
      key: phase.key,
      label: phase.label,
      detail: parts.join(" · "),
      order: phaseIndex >= 0 ? phaseIndex : PHASES.length,
    };
  }
  if (waits.length) {
    return {
      key: "waiting",
      label: `Waiting for ${waits.join(", ")}`,
      detail: "",
      order: 5,
    };
  }
  if (run.attention.includes("admission_unconfirmed")) {
    return {
      key: "admission_unconfirmed",
      label: "Admission unconfirmed",
      detail: "Observed before its admission was confirmed",
      order: 6,
    };
  }
  const counts = run.stage_counts ?? {};
  const total = Object.values(counts).reduce((sum, count) => sum + count, 0);
  if (total <= 1) {
    return {
      key: "admitted",
      label: "Admitted, nothing started yet",
      detail: "No pipeline activity observed since admission",
      order: 7,
    };
  }
  const done = counts.succeeded ?? 0;
  return {
    key: "no_live_stage",
    label: "No live stage data",
    detail: `${done} stage${done === 1 ? "" : "s"} recorded, none active right now`,
    order: 8,
  };
}

export function groupRunsByPhase(
  items: MonitorRun[],
): { phase: RunPhase; items: MonitorRun[] }[] {
  const groups = new Map<string, { phase: RunPhase; items: MonitorRun[] }>();
  for (const run of items) {
    const phase = runPhase(run);
    const group = groups.get(phase.key) ?? { phase, items: [] };
    group.items.push(run);
    groups.set(phase.key, group);
  }
  return [...groups.values()]
    .map((group) => ({
      ...group,
      items: [...group.items].sort((a, b) =>
        b.last_observed_at.localeCompare(a.last_observed_at),
      ),
    }))
    .sort(
      (a, b) =>
        a.phase.order - b.phase.order ||
        a.phase.label.localeCompare(b.phase.label),
    );
}

export function heartbeatStale(run: MonitorRun): boolean {
  return Object.values(run.execution_checks ?? {}).some(
    (check) =>
      check.stale === true &&
      (check.status === "running" || check.status === "queued"),
  );
}
